/**
 * Typography Configuration
 *
 * Defines standard MUI typography variants plus custom brand variants.
 * Follows Material Design type scale with BPS brand customizations.
 *
 * BPS Typography System:
 * - Headers: PP Neue Machina Plain Ultrabold
 * - Sub-headers: PP Frankton Sans Bold
 * - Body: Roboto Regular
 *
 * For BPR/Swerve typefaces, we use variations of the font families
 * originally used for blueprint, maintaining consistency while providing
 * a fresh look through different sub-fonts in the same families.
 *
 * Best Practices:
 * - Use rem units for scalable typography
 * - Maintain consistent line heights for readability
 * - Include letter-spacing for better legibility at small sizes
 * - Use semantic variant names (h1-h6, body1-2, etc.)
 *
 * @see https://mui.com/material-ui/customization/typography/
 */

import type { ThemeOptions, TypographyVariantsOptions } from '@mui/material/styles';

/**
 * Font family definitions
 * Headers use PP Neue Machina Plain Ultrabold
 * Sub-headers use PP Frankton Sans Bold
 * Body and other text use Roboto Regular
 */
const headerFontFamily = ['"PP Neue Machina Plain"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'].join(',');

const subHeaderFontFamily = ['"PP Frankton Sans"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'].join(',');

const bodyFontFamily = [
  '"Roboto"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

/**
 * Base typography variants - defined separately so semantic variants can inherit from them
 */
const baseVariants = {
  // Heading variants (h1-h6) - use PP Neue Machina Plain Ultrabold
  h1: {
    fontFamily: headerFontFamily,
    fontSize: '2.5rem', // 40px
    fontWeight: 800, // Ultrabold
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontFamily: headerFontFamily,
    fontSize: '2rem', // 32px
    fontWeight: 800, // Ultrabold
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontFamily: headerFontFamily,
    fontSize: '1.75rem', // 28px
    fontWeight: 800, // Ultrabold
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h4: {
    fontFamily: subHeaderFontFamily, // Sub-headers use PP Frankton Sans
    fontSize: '1.5rem', // 24px
    fontWeight: 700, // Bold
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontFamily: subHeaderFontFamily, // Sub-headers use PP Frankton Sans
    fontSize: '1.25rem', // 20px
    fontWeight: 700, // Bold
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontFamily: subHeaderFontFamily, // Sub-headers use PP Frankton Sans
    fontSize: '1.125rem', // 18px
    fontWeight: 700, // Bold
    lineHeight: 1.5,
    letterSpacing: '0.0075em',
  },

  // Body text variants
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
};

/**
 * Standard typography configuration
 */
export const typography: ThemeOptions['typography'] = {
  fontFamily: bodyFontFamily, // Default to Roboto for body text
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // Base heading and body variants
  ...baseVariants,

  // Subtitle variants
  subtitle: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },

  // Button text
  button: {
    fontSize: '0.9375rem', // 15px
    fontWeight: 700,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'uppercase',
  },

  // Caption
  caption: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },

  // Semantic brand variants - named by usage context for clarity
  // These inherit from h1-h6 and only override what's needed
  dialogTitle: {
    ...baseVariants.h5,
    lineHeight: '32px',
    letterSpacing: '0.15px',
    wordWrap: 'break-word',
  },
  sectionTitle: {
    ...baseVariants.h5,
    lineHeight: '32px',
    letterSpacing: '0.15px',
    wordWrap: 'break-word',
  },
  subHeaderTitle: {
    fontFamily: subHeaderFontFamily,
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    wordWrap: 'break-word',
  },
  panelTitle: {
    ...baseVariants.h6,
    lineHeight: '28px',
    letterSpacing: '0.15px',
    wordWrap: 'break-word',
  },
  tabText: {
    ...baseVariants.h6,
    lineHeight: '28px',
    letterSpacing: '0.15px',
    wordWrap: 'break-word',
  },
  navText: {
    fontSize: '0.9375rem', // 15px
    fontWeight: 700,
    lineHeight: '26px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
  },
  projectTitle: {
    fontSize: '0.9375rem', // 15px
    fontWeight: 700,
    lineHeight: '26px',
    letterSpacing: '0.25px',
    textTransform: 'uppercase',
  },
  buttonText: {
    fontSize: '0.75rem', // 12px
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: '0.35px',
    textTransform: 'uppercase',
  },
} as TypographyVariantsOptions;

/**
 * Maps custom typography variants to HTML elements for semantic markup
 */
export const variantMapping = {
  dialogTitle: 'h1',
  sectionTitle: 'h1',
  subHeaderTitle: 'h3',
  panelTitle: 'h2',
  tabText: 'span',
  navText: 'span',
  projectTitle: 'span',
  buttonText: 'span',
} as const;
